import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { BidType, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'
import type { AllocatedVolumes } from '@/features/bidding/types/allocatedVolumes'
import type { Ptu } from '@/features/bidding/types/bid'

const WATTS_IN_MW = 1_000_000

interface BiddingDashboardPerGroupAndPtuTableProps {
  activationGroups: ActivationGroup[] | undefined
  allocatedVolumes: AllocatedVolumes
}

export const BiddingDashboardPerGroupAndPtuTable = ({
  activationGroups,
  allocatedVolumes,
}: BiddingDashboardPerGroupAndPtuTableProps) => {
  const timezone = `UTC${allocatedVolumes.deliveryDay.getStartOfDay().toFormat('ZZ')}`
  // since we are combining markets with 15m PTUs and markets with 1h PTUs we default to 15 min granularity
  const ptus = allocatedVolumes.deliveryDay.ptus({ minutes: 15 })

  const tableColumns = (activationGroups || []).flatMap(
    (group): { group: ActivationGroup; bidType: BidType | undefined; label: string; key: string }[] => {
      if (MFRR_MARKET_PROGRAMS.includes(group.marketProgram)) {
        return [
          { group, bidType: BidType.CAPACITY, label: `${group.code}`, key: `${group.uuid}-${BidType.CAPACITY}` },
          { group, bidType: BidType.ENERGY, label: `${group.code} (ENERGY)`, key: `${group.uuid}-${BidType.ENERGY}` },
        ]
      }
      return [{ group, bidType: undefined, label: group.code, key: group.uuid }]
    },
  )

  const getVolumeForGroupAndPtu = (groupUuid: string, ptu: Ptu, bidType?: BidType) => {
    const allocationsForGroup = allocatedVolumes.allocatedVolumes.find(
      (allocatedVolume) =>
        allocatedVolume.activationGroupUuid === groupUuid && (!bidType || allocatedVolume.bidType === bidType),
    )
    if (!allocationsForGroup) return ''

    let ptuStart = ptu.start
    if (allocationsForGroup.bidType === BidType.CAPACITY) {
      // for capacity bids the PTUs are 1h duration, so when asked for the volume for PTU 03:45 we have to return the volume for PTU 03:00 instead
      ptuStart = ptu.start.set({ minute: 0 })
    }
    const ptuAndVolume = allocationsForGroup.ptus.find((it) => +it.startAt === +ptuStart)
    if (!ptuAndVolume) return ''
    if (ptuAndVolume.volume.quantity === 0) return '0'
    return (ptuAndVolume.volume.quantity / WATTS_IN_MW).toFixed(2)
  }

  const handleCopy = () => {
    if (!activationGroups || ptus.length === 0) return

    const headers = ['PTU', ...tableColumns.map((c) => c.label)].join('\t')
    const rows = ptus.map((ptu) => {
      const ptuRange = `${ptu.start.toFormat('HH:mm')}`
      const values = tableColumns.map((col) => getVolumeForGroupAndPtu(col.group.uuid, ptu, col.bidType)).join('\t')
      return `${ptuRange}\t${values}`
    })

    const tsv = [headers, ...rows].join('\n')
    navigator.clipboard.writeText(tsv)
  }

  if (!activationGroups || allocatedVolumes.allocatedVolumes.length === 0) {
    return (
      <Typography sx={{ p: 2 }} variant="body2">
        No allocated volumes found for the selected delivery day.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography>
          Market Volume (MW) per Group and PTU. This includes offered values if the bid has not been accepted yet, and
          wholesale trades.
        </Typography>
        <Button size="small" startIcon={<ContentCopyIcon />} variant="outlined" onClick={handleCopy}>
          Copy to Clipboard
        </Button>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Table stickyHeader aria-label="per group and ptu table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography fontWeight="bold">PTU</Typography>
                  <Typography variant={'caption'}>{timezone}</Typography>
                </TableCell>
                {tableColumns.map((col) => (
                  <TableCell key={`${col.key}`} align="center">
                    <Typography noWrap fontWeight="bold" title={col.group.marketProgram}>
                      {col.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ptus.map((ptu) => (
                <TableRow key={ptu.start.toISO()}>
                  <TableCell component="th" scope="row">
                    {ptu.start.toFormat('HH:mm')}
                  </TableCell>
                  {tableColumns.map((col) => (
                    <TableCell key={`${col.key}`} align="center">
                      {getVolumeForGroupAndPtu(col.group.uuid, ptu, col.bidType)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}
